import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { faCheck, faCopy, faEye } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as CampaignPinAction from 'components/sidebar/CampaignPinAction';
import { useCopy } from 'components/snippet';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { truncate } from 'lodash';

const CampaignHeader = () => {
    const { data: campaign } = useCurrentCampaign();
    const { canPinCampaigns } = useCurrentAccount();

    const {
        matchFulfilledNotificationDismissedCampaignIds,
        setMatchFulfilledNotificationDismissedCampaignIds,
    } = useLocalStorage(state => {
        return {
            setMatchFulfilledNotificationDismissedCampaignIds:
                state.setMatchFulfilledNotificationDismissedCampaignIds,
            matchFulfilledNotificationDismissedCampaignIds:
                state.matchFulfilledNotificationDismissedCampaignIds,
        };
    });

    const showMatchFulfilledNotification =
        Number(campaign?.match?.amount) > 0 &&
        Number(campaign?.match?.amount_remaining) === 0 &&
        matchFulfilledNotificationDismissedCampaignIds.find(
            dismissedId => dismissedId === campaign?.id,
        ) === undefined;

    const canPinText = canPinCampaigns ? 'Pin to sidebar' : 'Cannot pin more than 5 campaigns';
    const pinText = campaign?.is_pinned ? 'Unpin from sidebar' : canPinText;

    return (
        <div className="flex flex-col gap-7">
            {showMatchFulfilledNotification && (
                <AlertNotification.Root
                    type="success"
                    closable={true}
                    onClose={() => {
                        setMatchFulfilledNotificationDismissedCampaignIds([
                            ...matchFulfilledNotificationDismissedCampaignIds,
                            campaign?.id!,
                        ]);
                    }}
                >
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>
                            You've exhausted your campaign match!
                        </AlertNotification.Title>
                        <AlertNotification.Description>
                            Matching is now disabled but you can re-enable it to increase your match
                            amount if needed.
                        </AlertNotification.Description>
                    </AlertNotification.Content>
                    <AlertNotification.Close />
                </AlertNotification.Root>
            )}
            <div className={'flex flex-col gap-2 md:flex-row md:justify-between'}>
                <div className={'flex flex-col'}>
                    <div className={'flex items-center gap-2'}>
                        <p className={'text-xs font-medium text-gray-600'}>{campaign?.code}</p>
                        <CampaignStatus active={campaign?.active ?? false} />
                    </div>
                    <p className="flex items-center gap-1.5 text-2xl font-medium">
                        {campaign?.title}
                        {campaign && (
                            <CampaignPinAction.Root>
                                <CampaignPinAction.Trigger
                                    campaign={campaign}
                                    className={campaign.is_pinned ? 'text-gray-900' : ''}
                                    data-intercom-target={
                                        IntercomProductTourTargets.CampaignPinnedButton
                                    }
                                >
                                    {pinText}
                                </CampaignPinAction.Trigger>
                                <CampaignPinAction.TooltipContent>
                                    <p>{pinText}</p>
                                </CampaignPinAction.TooltipContent>
                            </CampaignPinAction.Root>
                        )}
                    </p>
                </div>
                <div className={'flex items-center justify-center [&>*]:w-full'}>
                    <CampaignLink url={campaign?.url ?? ''} />
                </div>
            </div>
        </div>
    );
};

const CampaignStatus = ({ active }: { active: boolean }) => {
    if (active) {
        return <Badge color="success">Active</Badge>;
    }

    return <Badge color="error">Inactive</Badge>;
};

const CampaignLink = ({ url }: { url: string }) => {
    const { didCopy, onClickCopy } = useCopy();
    return (
        <Badge color="primary" className="h-auto p-1">
            <a className="px-2 text-sm" target="_blank" rel="noopener noreferrer" href={url}>
                <span>{truncate(url, { length: 35 })}</span>
            </a>
            <div className={'hidden items-center md:flex'}>
                <Button
                    onClick={e => {
                        onClickCopy(e, url);
                    }}
                    variant={'outline'}
                    color={'primary'}
                    size={'sm'}
                    className="rounded-bl-full rounded-br-none rounded-tl-full rounded-tr-none px-4 py-0 ring-transparent hover:bg-gray-100"
                >
                    <FontAwesomeIcon icon={didCopy ? faCheck : faCopy} />
                    Copy
                </Button>
                <a target="_blank" rel="noopener noreferrer" href={url}>
                    <Button
                        variant={'outline'}
                        color={'primary'}
                        size={'sm'}
                        className="rounded-bl-none rounded-br-full rounded-tl-none rounded-tr-full border-l border-l-gray-200 px-4 py-0 ring-transparent hover:bg-gray-100"
                    >
                        <FontAwesomeIcon icon={faEye} />
                        View
                    </Button>
                </a>
            </div>
        </Badge>
    );
};

export { CampaignHeader };
